"use client";

import { cn } from "@/utils/cn";
import { ImageGalleryImage, useImageGallery } from "..";

interface GalleryDotsProps {
	className?: string;
	maxVisibleDots?: number;
}

export function GalleryDots({ className, maxVisibleDots = 7 }: GalleryDotsProps) {
	const { currentIndex, images, goToIndex } = useImageGallery();

	const totalImages = images.length;
	const center = Math.floor(maxVisibleDots / 2);

	if (totalImages <= 1) return null;

	return (
		<div
			className={cn("absolute bottom-12 left-1/2 transform -translate-x-1/2 flex items-center", className)}
			style={{ overflow: "hidden" }}
		>
			<div className="flex items-center space-x-1 transition-transform duration-300">
				{(() => {
					if (totalImages <= maxVisibleDots) {
						return images.map((_: ImageGalleryImage, index: number) => (
							<div
								key={index}
								role="button"
								tabIndex={0}
								className={cn(
									"rounded-full transition-all duration-300 cursor-pointer outline-none",
									index === currentIndex ? "w-3 h-3 bg-white" : "w-2 h-2 bg-white/60 hover:bg-white/80",
								)}
								onMouseDown={e => {
									e.preventDefault();
									e.stopPropagation();
								}}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									e.nativeEvent.stopImmediatePropagation();
									goToIndex(index);
								}}
								onKeyDown={e => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										e.stopPropagation();
										goToIndex(index);
									}
								}}
								aria-label={`Go to slide ${index + 1}`}
							/>
						));
					}

					// Circular window logic for many images
					return Array.from({ length: maxVisibleDots }, (_, i) => {
						const dotIndex = (currentIndex - center + i + totalImages) % totalImages;
						const distanceFromCenter = Math.abs(i - center);

						return (
							<div
								key={dotIndex}
								role="button"
								tabIndex={0}
								className={cn(
									"rounded-full transition-all duration-300 cursor-pointer outline-none",
									distanceFromCenter === 0
										? "w-3 h-3 bg-white"
										: distanceFromCenter === 1
											? "w-2.5 h-2.5 bg-white/80"
											: distanceFromCenter === 2
												? "w-2 h-2 bg-white/60"
												: "w-1.5 h-1.5 bg-white/40",
								)}
								onMouseDown={e => {
									e.preventDefault();
									e.stopPropagation();
								}}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									e.nativeEvent.stopImmediatePropagation();
									goToIndex(dotIndex);
								}}
								onKeyDown={e => {
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault();
										e.stopPropagation();
										goToIndex(dotIndex);
									}
								}}
								aria-label={`Go to slide ${dotIndex + 1}`}
							/>
						);
					});
				})()}
			</div>
		</div>
	);
}
